"use client"
import styled from "@emotion/styled"
import { Modal } from "@mui/material"
import { Map, useMap } from "@vis.gl/react-google-maps"
import {
    Attribute,
    LocatorStationsWrapper
} from "api"
import { LocationContext } from "contexts/LocationProvider"
import { Suspense, useContext, useEffect, useState } from "react"

import StyledAdvancedMarker from "components/Locator/AdvancedMarker"
import SideActions from "components/Locator/SideActions"
import dynamic from "next/dynamic"

const ItemsList = dynamic(() => import("components/Locator/ItemsList"), { ssr: false })
const LocationsSwiper = dynamic(() => import("components/Locator/LocationsSwiper"), { ssr: false })
const SelectedDetails = dynamic(() => import("components/Locator/SelectedDetails"), { ssr: false })
import TopActions, { Actions } from "components/Locator/TopActions"
import { LayoutContext } from "contexts/LayoutProvider"
import {
    Desktop,
    Mobile,
    useIsDesktop
} from "ui"
import {
    useMapCamera,
    useMapResponseHandler,
    useMapZoom,
    useStationInteractions,
    useStationQuery,
    useUrlParams,
    useUserLocation
} from "./hooks"
import { onDownload as onDownloadService, onPrint as onPrintService } from "./service"

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  /* PZC-82: keep the locator clear of the page footer (was flush, footer clipped it) */
  padding-bottom: 40px;
`
const StyledMapContainer = styled.div`
  position: relative;
  width: 100%;
  height: inherit;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 670px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    height: 860px;
  }
`
const StyledStationCarousel = styled.div`
  position: absolute;
  bottom: 10px;
  left: 55px;
   z-index: 2;
   width: calc(100% - 60px);
`

const StyledGrid = styled.div`
  position: relative;
  display: grid;
  height: 540px;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 320px 1fr;
     height: auto;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    grid-template-columns: 408px 1fr;
     // height: auto;
  }
`
const ModalCont = styled.div`
  background-color: white;
  height: 80%;
  width: 100%;
  padding: 20px;
  border-radius: 20px 20px 0 0;
  position: absolute;
  bottom: 0;
  overflow-y: scroll;
`

type Props = {
    metadata: Attribute[]
    stations: LocatorStationsWrapper
    initialFiltersAreReady?: boolean
}

export interface UserLocation {
    lat: number;
    lon: number;
}

const MIN_ZOOM_TO_SHOW_LABEL = 13

function MapComponent({ metadata, stations, initialFiltersAreReady = true }: Props) {

    // location context
    const {
        setSelectedLocation,
        showAsList,
        filters,
        selectedLocation,
        selectedCity,
        setMobileDetails,
        mobileDetails,
        searchValue,
        setSearchValue,
        queryParams,
        initialValueFilters,
        setIsMapLoaded,
        isMapLoaded,
        updateFilters,
        resetFilters
    } = useContext(LocationContext)

    const { isApiReady } = useContext(LayoutContext)
    const isDesktop = useIsDesktop()
    const map = useMap()

    // State variables - declare before using in hooks
    const [showStationCarousel, setShowCarousel] = useState(false)

    // Custom hooks
    const userLoc = useUserLocation()
    const { handleZoomChange, shouldShowLabel } = useMapZoom({
        minZoomToShowLabel: MIN_ZOOM_TO_SHOW_LABEL
    })
    const { focusOnSpecificLocation } = useMapCamera()

    const { stationIdRef } = useUrlParams({
        stations,
        initialValueFilters,
        setSelectedLocation,
        setSearchValue,
        updateFilters,
        resetFilters
    })

    const { list, handleResponse } = useMapResponseHandler({ stationIdRef })
    const { handleStationClick } = useStationInteractions({
        isDesktop,
        setSelectedLocation,
        setShowCarousel
    })

    const handleClose = () => {
        setMobileDetails(false)
    }

    useStationQuery({
        filters,
        selectedCity,
        queryParams,
        initialFiltersAreReady,
        isApiReady: isApiReady ?? false,
        initialStations: stations,
        onResponse: (data, isStation) => handleResponse(data, isStation, map, queryParams)
    })

    const onPrint = async () => {
        await onPrintService(list)
    }

    const onDownload = async () => {
        await onDownloadService(selectedCity?.name, searchValue, filters || undefined)
    }

    useEffect(() => {
        if (isMapLoaded && selectedLocation && map) {
            focusOnSpecificLocation(
                map,
                selectedLocation.geoLocation.latitude,
                selectedLocation.geoLocation.longitude
            )
        }
    }, [isMapLoaded, selectedLocation, map, focusOnSpecificLocation])

    return (
        <StyledContainer>
            <Suspense>
                <TopActions
                    metadata={metadata}
                    userLoc={userLoc}
                    locations={list}
                    onPrint={onPrint}
                    onDownload={onDownload}
                    focusOnSpecificLocation={(lat, lon) => focusOnSpecificLocation(map, lat, lon)}
                    setShowCarousel={setShowCarousel}
                />
            </Suspense>
            <StyledGrid>
                <Desktop>
                    <SideActions
                        metadata={metadata}
                        userLoc={userLoc}
                        locations={list}
                    />
                </Desktop>
                <>
                    <StyledMapContainer>
                        {showAsList ? (
                            <ItemsList list={list} />
                        ) : (
                            <Map
                                streetViewControl={false}
                                fullscreenControl={false}
                                mapId="811c65245afc97ba"
                                mapTypeControl={false}
                                onZoomChanged={handleZoomChange}
                                defaultZoom={9}
                                defaultCenter={{
                                    lat: 31.9648616,
                                    lng: 34.9004544,
                                }}
                                onTilesLoaded={() => {
                                    setIsMapLoaded(true); // Mark the map as ready
                                }}
                            >
                                {list.length > 0 &&
                                    list?.map((item, key) => {
                                        const color = item.type === 1 ? "#FCBB00" : "#B30102"
                                        return (
                                            <StyledAdvancedMarker
                                                active={selectedLocation?.id === item.id}
                                                onClick={() => handleStationClick(item)}
                                                key={key.toString()}
                                                lng={item?.geoLocation?.longitude}
                                                lat={item?.geoLocation?.latitude}
                                                fill={color}
                                                title={item.name}
                                                showLabel={shouldShowLabel}
                                                isOutOfStationCharge={item.type === 10}
                                            />
                                        )
                                    })}
                            </Map>
                        )}
                    </StyledMapContainer>
                    <Mobile>
                        {showStationCarousel &&
                            <StyledStationCarousel>
                                <LocationsSwiper locations={list} />
                            </StyledStationCarousel>}
                    </Mobile>
                </>
            </StyledGrid>
            <Mobile>
                <Actions onPrint={onPrint} onDownload={onDownload} />
                <Modal
                    open={mobileDetails}
                    onClose={handleClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <ModalCont>
                        <SelectedDetails handleClose={handleClose} />
                    </ModalCont>
                </Modal>
            </Mobile>
        </StyledContainer>
    )
}

export default MapComponent
